import React, { useEffect, useState } from "react";
import { auth, db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import { Link } from "react-router-dom";
import Window from "../components/Window";
import Icon, { IconName, IconTile } from "../components/Icon";
import { useNotifications } from "../notifications";
import { promptOfTheWeek } from "../prompts";
import "../styles.css";

const tileColors = ["#ffe3ea", "#dff4fd", "#fff6d6", "#e4f6e4", "#f3e6ff", "#fbe9d7"];

const sections: {
  icon: IconName;
  tone: "pink" | "blue" | "gold" | "green" | "violet";
  title: string;
  description: string;
  link: string;
}[] = [
  {
    icon: "inbox",
    tone: "pink",
    title: "Inbox",
    description: "Check your latest letters and kudos.",
    link: "/inbox",
  },
  {
    icon: "pin",
    tone: "blue",
    title: "PenPal Board",
    description: "Read the newest ads and post your own.",
    link: "/board",
  },
  {
    icon: "request",
    tone: "gold",
    title: "Requests",
    description: "Accept new pals or save them for later.",
    link: "/requests",
  },
];

const TIPS: { icon: IconName; text: string }[] = [
  { icon: "gift", text: "send kudos generously, it makes someone's whole day" },
  { icon: "drop", text: "water your plant when you visit. it never wilts, so no pressure" },
  { icon: "spoon", text: "set your spoon meter on My Profile so pals know your energy lately" },
  { icon: "request", text: "add a little note to your pen pal request to say why you'd like to write" },
  { icon: "check", text: '"maybe later" and "no thanks" are always okay, and nobody is told which you picked' },
  { icon: "eye", text: "you can post on the board anonymously and decide when to reveal yourself" },
  { icon: "book", text: "your Address Book has all your pen pals. click one to write them a letter" },
  { icon: "shield", text: "if someone makes you uncomfortable, Block or Report them. they won't be told" },
  { icon: "happy", text: 'build your avatar on My Profile, or hit "Surprise me" for someone new' },
  { icon: "clock", text: "share your reply pace on your profile so nobody worries when letters take a while" },
];

// A random tip on every visit, never the same one twice in a row.
function pickTip() {
  let last = -1;
  try {
    last = Number(sessionStorage.getItem("penpal-last-tip") ?? -1);
  } catch {
    // storage unavailable; any tip is fine
  }
  let i = Math.floor(Math.random() * TIPS.length);
  if (i === last) i = (i + 1) % TIPS.length;
  try {
    sessionStorage.setItem("penpal-last-tip", String(i));
  } catch {
    // ignore
  }
  return TIPS[i];
}

const Dashboard = () => {
  const [tip] = useState(pickTip);
  const [username, setUsername] = useState("");
  const { letters, kudos, requests, board } = useNotifications();

  useEffect(() => {
    const fetchUsername = async () => {
      const uid = auth.currentUser?.uid;
      if (!uid) return;

      const docRef = doc(db, "users", uid);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        setUsername(docSnap.data().username);
      }
    };

    fetchUsername();
  }, []);

  // Each kind of news gets its own blinking badge on its tile.
  const many = (n: number, one: string, more = one + "S") => `${n} NEW ${n === 1 ? one : more}!`;
  const fresh: Record<string, string[]> = {
    "/inbox": [letters > 0 && many(letters, "LETTER"), kudos > 0 && many(kudos, "KUDOS", "KUDOS")].filter(
      Boolean
    ) as string[],
    "/board": board > 0 ? [many(board, "POST")] : [],
    "/requests": requests > 0 ? [many(requests, "REQUEST")] : [],
  };

  return (
    <>
      <Window title="welcome.txt">
        <h1 className="window-heading">
          {username ? `Hi, @${username}!` : "Welcome!"}{" "}
          <span aria-hidden="true">✿</span>
        </h1>
        <p className="window-sub !mb-0">
          Welcome to the 2SLGBTQIA+ Pen Pal Network. Grab a cup of tea and
          write someone something nice.
        </p>
        <p className="mt-2 text-sm">
          <span className="font-pixel text-[0.65rem]">~*~ prompt of the week ~*~</span>{" "}
          <em>{promptOfTheWeek()}</em>
        </p>
      </Window>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {sections.map((section, idx) => (
          <Link
            to={section.link}
            key={section.link}
            className={`tile relative${fresh[section.link]?.length ? " tile-fresh" : ""}`}
            style={{ background: tileColors[idx % tileColors.length] }}
          >
            {fresh[section.link]?.length > 0 && (
              <span className="absolute right-3 top-3 flex flex-col items-end gap-1.5">
                {fresh[section.link].map((label) => (
                  <span key={label} className="new-badge blink">
                    {label}
                  </span>
                ))}
              </span>
            )}
            <IconTile name={section.icon} tone={section.tone} size={44} />
            <h2 className="tile-title">{section.title}</h2>
            <p className="text-sm">{section.description}</p>
          </Link>
        ))}
      </div>

      <p className="hidden text-center text-sm text-ink-soft sm:block">
        <Icon name={tip.icon} size={16} /> tip: {tip.text}
      </p>
    </>
  );
};

export default Dashboard;
